import React, { useState, useMemo } from 'react';
import { Search, Plus, ChevronRight } from 'lucide-react';
import ScreenHeader from '../ScreenHeader';
import { CONDUCTS } from '../../data/observationEngine';

/**
 * Selección de conducta observable (§30).
 * - Hasta 10 chips por dominio
 * - Buscador opcional
 * - Opción "Otra conducta observable" (nota obligatoria luego)
 * - No muestra puntajes ni "sentinel" al observador
 */
const ScreenConducta = ({ dominio, onBack, onNext, value, setValue }) => {
  const [q, setQ] = useState('');
  const lista = CONDUCTS[dominio.id] || [];
  const filtered = useMemo(() => {
    if (!q.trim()) return lista;
    const s = q.toLowerCase();
    return lista.filter((c) => c.label.toLowerCase().includes(s));
  }, [q, lista]);

  const isOther = value?.code === 'OTRA';

  return (
    <div className="step-enter flex flex-col h-full">
      <ScreenHeader
        step={2}
        stepName="Conducta observable"
        onBack={onBack}
        title={dominio.name}
        subtitle="Selecciona la conducta observable que mejor describe el hecho."
        eta="≈ 35s restantes"
        tone={{ color: dominio.color }}
      />

      <div className="flex-1 px-5 overflow-hidden flex flex-col">
        <div className="m-input-wrap">
          <Search className="m-input-icon" />
          <input
            data-testid="conducta-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar conducta..."
            className="m-input has-leading-icon"
          />
        </div>

        <p className="m-label mt-5">Conductas observables</p>
        <div className="flex-1 overflow-y-auto space-y-2 pb-4 pr-1" data-testid="conducta-list">
          {filtered.map((c) => (
            <button
              key={c.code}
              data-testid={`conducta-${c.code}`}
              onClick={() => setValue(c)}
              className={`w-full text-left px-4 py-3.5 rounded-2xl border-2 transition-all flex items-center justify-between gap-3 ${
                value?.code === c.code ? 'is-selected' : ''
              }`}
              style={{
                background: value?.code === c.code ? dominio.bg : '#FFFFFF',
                borderColor: value?.code === c.code ? dominio.border : '#F1F4FA',
              }}
            >
              <span className="text-[14.5px] font-bold text-brand-deep leading-snug">
                {c.label}
              </span>
              {value?.code === c.code && (
                <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: dominio.color, color: '#fff' }}>
                  <CheckSmall />
                </span>
              )}
            </button>
          ))}

          {/* Otra conducta observable */}
          <button
            data-testid="conducta-otra"
            onClick={() => setValue({ code: 'OTRA', label: 'Otra conducta observable' })}
            className={`w-full text-left px-4 py-3.5 rounded-2xl border-2 transition-all flex items-center gap-3 ${isOther ? 'is-selected' : ''}`}
            style={{
              background: isOther ? '#EEEAFE' : '#FFFFFF',
              borderColor: isOther ? '#5346E6' : '#F1F4FA',
              borderStyle: 'dashed',
            }}
          >
            <span className="w-9 h-9 rounded-xl bg-brand-light text-brand-violet flex items-center justify-center shrink-0">
              <Plus className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <p className="text-[14px] font-bold text-brand-deep">Otra conducta observable</p>
              <p className="text-[11px] text-brand-muted">Requerirá una nota breve.</p>
            </div>
          </button>
        </div>
      </div>

      <div className="px-5 pb-6 pt-2 border-t border-[#F1F4FA] bg-white">
        <button
          data-testid="conducta-next"
          onClick={onNext}
          disabled={!value}
          className="m-btn-primary"
        >
          Continuar
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

const CheckSmall = () => (
  <svg viewBox="0 0 16 16" className="w-3.5 h-3.5">
    <path d="M3.5 8.5 L6.5 11.5 L12.5 5" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default ScreenConducta;
